import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { API, useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/seo";
import { toast } from "sonner";
import BookingQuestionsForm, { validateAnswers, answersPayload } from "../components/BookingQuestionsForm";
import { Lock } from "lucide-react";
import { useCheckoutQuote, apptTzId, money, consentSignature } from "../lib/checkoutQuote";

export default function Cart() {
  useSeo({
    title: "Your cart — BookFreely",
    description: "Review your BookFreely bookings and check out securely.",
    path: "/cart",
    noindex: true,
  });
  const { items, saved, totals, remove, clear, saveForLater, moveToCart, revalidate } = useCart();
  const { user, authHeader } = useAuth();
  const nav = useNavigate();
  const [promoInput, setPromoInput] = useState("");
  const [applied, setApplied] = useState(null); // { code, discount, description }
  const [promoBusy, setPromoBusy] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [priceChanges, setPriceChanges] = useState(null); // Revalidation dialog state
  // Iteration 80 — booking questions per cart item. Keyed by service_id for the
  // question definitions, and by cart row key for the customer's answers.
  const [questionsByService, setQuestionsByService] = useState({});
  const [answersByRow, setAnswersByRow] = useState({});
  // Jun 2026 — per-item future-charge authorization. Keyed by the exact thing
  // the customer agreed to, so changing a time or an amount clears the tick.
  const [consents, setConsents] = useState({});
  const rowKey = (it) => `${it.service_id}-${it.scheduled_at}`;

  // Every amount, timing line and authorization sentence comes from the backend
  // quote, which uses the SAME helper that builds the Stripe Session.
  const tz = apptTzId();
  // Bumped on every revalidation so the quote is re-fetched against live prices.
  const [priceStamp, setPriceStamp] = useState(0);
  // Request-first businesses confirm the time before any payment, so those rows
  // are never quoted, never authorized and never sent to checkout.
  const payable = items.filter((i) => !i.request_only && i.business_booking_method !== "request");
  const requestOnlyItems = items.filter((i) => i.request_only || i.business_booking_method === "request");
  const { quote } = useCheckoutQuote({
    mode: "cart",
    items: payable.map((i) => ({ service_id: i.service_id, scheduled_at: i.scheduled_at, appointment_tz: tz })),
    promoCode: applied ? applied.code : null,
    authHeader,
    enabled: payable.length > 0,
    refreshKey: priceStamp,
  });
  const quoteItems = (quote && quote.items) || [];
  const qByRow = {};
  quoteItems.forEach((qi, i) => { const it = payable[i]; if (it) qByRow[rowKey(it)] = qi; });
  const qFor = (it) => qByRow[rowKey(it)] || null;
  const T = (quote && quote.totals) || null;
  const needsTick = (qi) => !!qi && (qi.consent_required || qi.acknowledgement_required);
  const requiredSigs = quoteItems.filter(needsTick).map(consentSignature);
  const allConsented = requiredSigs.every((sig) => !!consents[sig]);
  const blockedItems = items.filter((i) => i.blocked);

  // Jun 2026 — never trust a price saved in the browser. Verify every row
  // against the live service on cart LOAD (and again right before Stripe).
  const loadCheckedRef = React.useRef(false);
  React.useEffect(() => {
    if (!user || items.length === 0 || loadCheckedRef.current) return;
    loadCheckedRef.current = true;
    (async () => {
      const rev = await revalidate();
      if (rev && Array.isArray(rev.changes) && rev.changes.length > 0) {
        setConsents({});                 // every tick must be given again
        setPriceChanges(rev.changes);
      }
      setPriceStamp((n) => n + 1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, items.length]);

  React.useEffect(() => {
    const ids = [...new Set(items.map((i) => i.service_id))].filter((id) => !(id in questionsByService));
    if (!ids.length) return;
    (async () => {
      const found = {};
      await Promise.all(ids.map(async (id) => {
        try {
          const r = await axios.get(`${API}/services/${id}`);
          found[id] = r.data?.booking_questions || [];
        } catch { found[id] = []; }
      }));
      setQuestionsByService((prev) => ({ ...prev, ...found }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Only used to gate the promo form — never to display an amount.
  const subtotal = totals.totalDeposit;

  const applyPromo = async () => {
    if (!user) { toast.error("Please sign in to apply a promo code"); return; }
    if (!promoInput.trim()) return;
    if (subtotal <= 0) {
      toast.error("Add a service with a deposit to use a promo code");
      return;
    }
    setPromoBusy(true);
    try {
      const r = await axios.post(
        `${API}/promo-codes/validate`,
        { code: promoInput.trim().toUpperCase(), subtotal },
        { headers: authHeader() },
      );
      setApplied({ code: r.data.code, discount: r.data.discount, description: r.data.description });
      toast.success(`Promo "${r.data.code}" applied — saved $${r.data.discount.toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply promo code");
      setApplied(null);
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => { setApplied(null); setPromoInput(""); };

  const checkout = async () => {
    if (!user) {
      toast.info("Please sign in to check out");
      nav("/login?next=/cart");
      return;
    }
    if (user.role !== "customer" && user.role !== "super_admin") {
      toast.error("Only customers can book services");
      return;
    }
    setCheckingOut(true);
    try {
      for (const it of payable) {
        const qs = questionsByService[it.service_id] || [];
        const err = validateAnswers(qs, answersByRow[rowKey(it)] || {});
        if (err) { toast.error(`${it.service_title}: ${err}`); setCheckingOut(false); return; }
      }
      if (payable.length === 0) {
        toast.error("Nothing in your cart can be paid for yet — send those requests from the service page");
        setCheckingOut(false);
        return;
      }
      if (!quote) { toast.error("Still calculating your amounts — one moment"); setCheckingOut(false); return; }
      if (blockedItems.length > 0) {
        toast.error("Remove the unavailable item(s) in your cart to continue");
        setCheckingOut(false);
        return;
      }
      if (!allConsented) {
        toast.error("Please authorize each item's charge to continue");
        setCheckingOut(false);
        return;
      }
      // Iteration 54 / 84 — LAST-CHANCE revalidation against the live services.
      // Any change clears the ticks and the customer must review and authorize
      // the new amounts before we go anywhere near Stripe.
      const rev = await revalidate();
      if (rev && Array.isArray(rev.changes) && rev.changes.length > 0) {
        setConsents({});
        setPriceStamp((n) => n + 1);
        setPriceChanges(rev.changes);
        setCheckingOut(false);
        return;
      }
      const payload = {
        items: payable.map((i) => {
          const qs = questionsByService[i.service_id] || [];
          const qi = qFor(i);
          return {
            service_id: i.service_id, scheduled_at: i.scheduled_at, notes: i.notes,
            answers: qs.length ? answersPayload(qs, answersByRow[rowKey(i)] || {}) : undefined,
            appointment_tz: tz,
            payment_consent: needsTick(qi) ? true : undefined,
            consent_version: qi ? qi.consent_version : undefined,
            authorized_today_cents: qi ? qi.authorized_today_cents : undefined,
            authorized_balance_cents: qi ? qi.authorized_balance_cents : undefined,
          };
        }),
        promo_code: applied ? applied.code : null,
        origin_url: window.location.origin,
        authorized_total_today_cents: T ? T.total_today_cents : undefined,
      };
      const r = await axios.post(`${API}/cart/checkout`, payload, { headers: authHeader() });
      if (r.data.url) {
        // Stripe checkout — server will remove purchased items from cart on webhook fulfill.
        window.location.assign(r.data.url);
        return;
      }
      // No-deposit path — clear the cart (server has already inserted booking rows).
      await clear();
      toast.success(r.data.message || "Bookings sent");
      nav("/account");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setCheckingOut(false);
    }
  };

  if (items.length === 0) {
    // Iteration 55 fix — even with an empty cart, show the Save-for-Later
    // section so the user can move items back into their cart.
    return (
      <div className="App">
        <Header hideSearch />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 text-center">
          <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Your cart</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "var(--gf-text)" }}>Your cart is empty</h1>
          <p className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>
            Browse services and queue up multiple bookings to check out at once.
          </p>
          <Link to="/services" data-testid="cart-browse-link" className="inline-block mt-6 rounded-full px-5 py-2.5 text-sm font-medium"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
            Browse services →
          </Link>
          {user && saved && saved.length > 0 && (
            <div className="mt-10 pt-8 border-t text-left" style={{ borderColor: "var(--gf-border)" }} data-testid="saved-for-later-section">
              <h3 className="font-display text-lg" style={{ color: "var(--gf-text)" }}>Saved for later</h3>
              <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>Move an item back to your cart to check out with it.</p>
              <div className="mt-4 space-y-3">
                {saved.map((it) => (
                  <div key={it.id}
                    className="rounded-xl border p-4 flex items-start gap-4"
                    style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", opacity: 0.95 }}
                    data-testid={`saved-item-${it.service_id}`}>
                    {it.image_url ? (
                      <img src={it.image_url} alt="" className="w-16 h-16 rounded-md object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-md flex items-center justify-center font-display text-xl flex-shrink-0"
                        style={{ background: "var(--gf-bg-soft)", color: "var(--gf-text-muted)" }}>
                        {(it.business_name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs uppercase tracking-[0.2em] truncate" style={{ color: "var(--gf-text-muted)" }}>{it.business_name}</div>
                      <div className="font-display text-lg mt-0.5 truncate" style={{ color: "var(--gf-text)" }}>{it.service_title}</div>
                      <div className="text-xs mt-1" style={{ color: "var(--gf-text-soft)" }}>
                        {new Date(it.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </div>
                      <div className="text-sm mt-1" style={{ color: "var(--gf-text)" }}>
                        <span className="font-semibold">${Number(it.price || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0 items-end">
                      <button data-testid={`move-to-cart-${it.service_id}`} onClick={() => moveToCart(it.id)}
                        className="text-xs underline opacity-70 hover:opacity-100" style={{ color: "var(--gf-accent)" }}>
                        Move to cart
                      </button>
                      <button data-testid={`remove-saved-${it.service_id}`} onClick={() => remove(it.id)}
                        className="text-xs underline opacity-70 hover:opacity-100" style={{ color: "var(--gf-text-muted)" }}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header hideSearch />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Your cart</div>
        <h1 className="font-display text-4xl mt-2" style={{ color: "var(--gf-text)" }}>
          {totals.count} {totals.count === 1 ? "service" : "services"}
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--gf-text-muted)" }}>
          From {totals.uniqueBusinesses} {totals.uniqueBusinesses === 1 ? "business" : "businesses"}.
        </p>

        <div className="mt-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
            {items.map((it, idx) => {
              const qi = qFor(it);
              const requestOnly = it.request_only || it.business_booking_method === "request";
              const sig = consentSignature(qi);
              return (
              <div key={rowKey(it)} data-testid={`cart-row-${it.service_id}`}>
              <div
                className="rounded-xl border p-4 flex items-start gap-4"
                style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
                data-testid={`cart-item-${it.service_id}`}>
                {it.image_url ? (
                  <img src={it.image_url} alt="" className="w-16 h-16 rounded-md object-cover flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-md flex items-center justify-center font-display text-xl flex-shrink-0"
                    style={{ background: "var(--gf-bg-soft)", color: "var(--gf-text-muted)" }}>
                    {(it.business_name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-[0.2em] truncate" style={{ color: "var(--gf-text-muted)" }}>
                    {it.business_name}
                  </div>
                  <div className="font-display text-lg mt-0.5 truncate" style={{ color: "var(--gf-text)" }}>{it.service_title}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--gf-text-soft)" }}>
                    {new Date(it.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </div>
                  <div className="text-sm mt-1" style={{ color: "var(--gf-text)" }}>
                    <span className="font-semibold">${Number(it.price || 0).toFixed(2)}</span>
                    {qi && (
                      <span className="ml-2" data-testid={`cart-item-today-${it.service_id}-${idx}`} style={{ color: "var(--gf-text-muted)" }}>
                        · {money(qi.total_today_cents)} charged today
                        {qi.remaining_balance_cents > 0
                          ? ` · ${money(qi.remaining_balance_cents)} remaining`
                          : ""}
                      </span>
                    )}
                  </div>
                  {qi && (
                    <div className="text-xs mt-1" data-testid={`cart-item-timing-${it.service_id}-${idx}`} style={{ color: "var(--gf-text-muted)" }}>
                      {qi.timing_note}
                    </div>
                  )}
                  {requestOnly && (
                    <div className="text-xs mt-1 rounded-md border px-2 py-1"
                         data-testid={`cart-item-request-only-${it.service_id}-${idx}`}
                         style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>
                      {it.business_name} confirms each time before payment. Send your request from the{" "}
                      <Link to={`/services/${it.service_id}`} className="underline"
                            data-testid={`cart-item-request-link-${it.service_id}-${idx}`}>service page</Link>{" "}
                      — nothing is charged and no card is saved until they accept. This item isn&rsquo;t part of the
                      checkout below.
                    </div>
                  )}
                  {it.blocked && (
                    <div className="text-xs mt-1 rounded-md border px-2 py-1 inline-block"
                         data-testid={`cart-item-blocked-${it.service_id}-${idx}`}
                         style={{ borderColor: "#c0392b", color: "#c0392b" }}>
                      No longer bookable — {it.blocked_reason || "unavailable"}. Remove it to check out.
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0 items-end">
                  {user && it.id && (
                    <button
                      data-testid={`save-for-later-${it.service_id}`}
                      onClick={() => saveForLater(it.id)}
                      className="text-xs underline opacity-70 hover:opacity-100"
                      style={{ color: "var(--gf-text-muted)" }}
                    >
                      Save for later
                    </button>
                  )}
                  <button
                    data-testid={`remove-cart-${it.service_id}`}
                    onClick={() => remove(it.id)}
                    className="text-xs underline opacity-70 hover:opacity-100"
                    style={{ color: "var(--gf-text-muted)" }}
                  >
                    Remove
                  </button>
                </div>
              </div>
              {(questionsByService[it.service_id] || []).length > 0 && (
                <div className="mt-2 rounded-xl border p-4" data-testid={`cart-questions-${it.service_id}`}
                     style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg-soft)" }}>
                  <BookingQuestionsForm
                    questions={questionsByService[it.service_id]}
                    answers={answersByRow[rowKey(it)] || {}}
                    onChange={(next) => setAnswersByRow((prev) => ({ ...prev, [rowKey(it)]: next }))}
                    authHeader={authHeader}
                    idPrefix={`cartbq-${it.service_id}`}
                  />
                </div>
              )}
              {qi && needsTick(qi) && (
                <label className="mt-2 flex items-start gap-2 cursor-pointer rounded-xl border p-4 text-xs leading-relaxed"
                       data-testid={`cart-consent-row-${it.service_id}-${idx}`}
                       style={{ borderColor: consents[sig] ? "var(--gf-border-strong)" : "var(--gf-accent)",
                                background: "var(--gf-surface-2)", color: "var(--gf-text)" }}>
                  <input
                    type="checkbox"
                    data-testid={`cart-consent-checkbox-${it.service_id}-${idx}`}
                    checked={!!consents[sig]}
                    onChange={(e) => setConsents((prev) => ({ ...prev, [sig]: e.target.checked }))}
                    className="mt-0.5"
                    style={{ accentColor: "var(--gf-accent)" }}
                  />
                  <span data-testid={`cart-consent-text-${it.service_id}-${idx}`}>{qi.consent_wording}</span>
                </label>
              )}
              </div>
              );
            })}
            <button
              data-testid="cart-clear-btn"
              onClick={() => { if (window.confirm("Clear cart?")) clear(); }}
              className="text-xs mt-1 underline"
              style={{ color: "var(--gf-text-muted)" }}
            >
              Clear cart
            </button>

            {/* Iteration 54 — Save-for-Later section (signed-in users only) */}
            {user && saved && saved.length > 0 && (
              <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--gf-border)" }} data-testid="saved-for-later-section">
                <h3 className="font-display text-lg" style={{ color: "var(--gf-text)" }}>Saved for later</h3>
                <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>Move an item back to your cart to check out with it.</p>
                <div className="mt-3 space-y-3">
                  {saved.map((it) => (
                    <div key={it.id}
                      className="rounded-xl border p-4 flex items-start gap-4"
                      style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", opacity: 0.9 }}
                      data-testid={`saved-item-${it.service_id}`}>
                      {it.image_url ? (
                        <img src={it.image_url} alt="" className="w-16 h-16 rounded-md object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-16 h-16 rounded-md flex items-center justify-center font-display text-xl flex-shrink-0"
                          style={{ background: "var(--gf-bg-soft)", color: "var(--gf-text-muted)" }}>
                          {(it.business_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs uppercase tracking-[0.2em] truncate" style={{ color: "var(--gf-text-muted)" }}>{it.business_name}</div>
                        <div className="font-display text-lg mt-0.5 truncate" style={{ color: "var(--gf-text)" }}>{it.service_title}</div>
                        <div className="text-xs mt-1" style={{ color: "var(--gf-text-soft)" }}>
                          {new Date(it.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </div>
                        <div className="text-sm mt-1" style={{ color: "var(--gf-text)" }}>
                          <span className="font-semibold">${Number(it.price || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0 items-end">
                        <button
                          data-testid={`move-to-cart-${it.service_id}`}
                          onClick={() => moveToCart(it.id)}
                          className="text-xs underline opacity-70 hover:opacity-100"
                          style={{ color: "var(--gf-accent)" }}
                        >
                          Move to cart
                        </button>
                        <button
                          data-testid={`remove-saved-${it.service_id}`}
                          onClick={() => remove(it.id)}
                          className="text-xs underline opacity-70 hover:opacity-100"
                          style={{ color: "var(--gf-text-muted)" }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <aside className="rounded-2xl border p-5 h-fit sticky top-24" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="cart-summary">
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Summary</div>
            <h2 className="font-display text-xl mt-1" style={{ color: "var(--gf-text)" }}>Checkout</h2>

            <div className="mt-4 space-y-2 text-sm">
              {payable.length === 0 ? (
                <div data-testid="cart-nothing-payable" style={{ color: "var(--gf-text-muted)" }}>
                  Nothing in your cart is paid for here yet. Send the request(s) above from the service page —
                  you&rsquo;ll finish payment once the business accepts.
                </div>
              ) : !T ? (
                <div data-testid="cart-summary-loading" style={{ color: "var(--gf-text-muted)" }}>Calculating amounts…</div>
              ) : (
                <>
                  <div className="flex justify-between" style={{ color: "var(--gf-text-soft)" }}>
                    <span>Service amount</span>
                    <span data-testid="cart-deposits-subtotal">{money(T.service_amount_today_cents + T.discount_cents)}</span>
                  </div>
                  {T.discount_cents > 0 && (
                    <div className="flex justify-between" style={{ color: "var(--gf-accent)" }} data-testid="cart-promo-row">
                      <span>Promo · {applied?.code}</span>
                      <span>− {money(T.discount_cents)}</span>
                    </div>
                  )}
                  <div className="flex justify-between" style={{ color: "var(--gf-text-soft)" }} data-testid="cart-processing-fee-row">
                    <span>Booking &amp; processing fee (3.85%)</span>
                    <span data-testid="cart-processing-fee">{money(T.processing_fee_cents)}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t font-display text-lg" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text)" }}>
                    <span>Total charged today</span>
                    <span data-testid="cart-due-today">{money(T.total_today_cents)}</span>
                  </div>
                  <div className="flex justify-between text-xs pt-1" style={{ color: "var(--gf-text-muted)" }} data-testid="cart-remaining-row">
                    <span>Remaining future charge</span>
                    <span data-testid="cart-balance-at-appt">{money(T.remaining_balance_cents)}</span>
                  </div>
                  {T.pay_in_person_cents > 0 && (
                    <div className="flex justify-between text-xs" style={{ color: "var(--gf-text-muted)" }} data-testid="cart-in-person-row">
                      <span>Paid directly to the business at the appointment</span>
                      <span>{money(T.pay_in_person_cents)}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Promo code */}
            <div className="mt-5">
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Promo / discount code</div>
              {applied ? (
                <div className="mt-2 rounded-md border p-3 flex items-center justify-between gap-2"
                  style={{ borderColor: "var(--gf-surface)", background: "var(--gf-surface)" }}>
                  <div>
                    <div className="text-sm font-mono" style={{ color: "var(--gf-accent)" }}>{applied.code}</div>
                    {applied.description && <div className="text-[11px]" style={{ color: "var(--gf-text-muted)" }}>{applied.description}</div>}
                  </div>
                  <button data-testid="cart-promo-clear" onClick={clearPromo} className="text-xs underline" style={{ color: "var(--gf-text-muted)" }}>
                    Remove
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <input
                    data-testid="cart-promo-input"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    placeholder="FRIENDS10"
                    className="flex-1 bg-transparent border rounded-md px-3 py-2 text-sm outline-none focus:border-[var(--gf-accent)]"
                    style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
                  />
                  <button
                    data-testid="cart-promo-apply"
                    onClick={applyPromo}
                    disabled={promoBusy || !promoInput.trim()}
                    className="text-xs rounded-full px-4 py-2 font-medium disabled:opacity-50"
                    style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)", border: "1px solid var(--gf-border-strong)" }}
                  >
                    {promoBusy ? "…" : "Apply"}
                  </button>
                </div>
              )}
            </div>

            {totals.uniqueBusinesses > 1 && totals.hasDepositItems && (
              <div className="mt-4 text-xs rounded-md border p-3"
                style={{ borderColor: "var(--gf-surface-2)", background: "var(--gf-surface-2)", color: "var(--gf-text-soft)" }}
                data-testid="cart-multi-biz-notice">
                One checkout, {totals.uniqueBusinesses} businesses — BookFreely splits every dollar behind the scenes so each business receives exactly what they&apos;re owed.
              </div>
            )}

            <button
              data-testid="cart-checkout-btn"
              onClick={checkout}
              disabled={checkingOut || payable.length === 0 || !T || !allConsented || blockedItems.length > 0}
              className="mt-5 w-full rounded-full font-medium py-3 transition-colors disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {checkingOut ? "Working…"
                : payable.length === 0 ? "Nothing to pay for yet"
                : !T ? "Calculating…"
                : T.total_today_cents > 0 ? `Pay ${money(T.total_today_cents)} & confirm bookings`
                : "Confirm bookings"}
            </button>
            <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px]"
               data-testid="stripe-secure-note" style={{ color: "var(--gf-text-muted)" }}>
              <Lock size={11} strokeWidth={2} aria-hidden="true" />
              Secure checkout powered by Stripe
            </p>
            {T && !allConsented && (
              <p className="mt-2 text-[11px]" data-testid="cart-consent-pending-note" style={{ color: "var(--gf-accent)" }}>
                Tick the authorization on each item to confirm the exact amount you&rsquo;re charged.
              </p>
            )}
            {blockedItems.length > 0 && (
              <p className="mt-2 text-[11px]" data-testid="cart-blocked-note" style={{ color: "#c0392b" }}>
                {blockedItems.length === 1 ? "One item" : `${blockedItems.length} items`} in your cart can no
                longer be booked. Remove {blockedItems.length === 1 ? "it" : "them"} to check out.
              </p>
            )}
            {T && T.total_today_cents > 0 && (
              <div data-testid="cart-wallets-hint" className="mt-2 flex items-center justify-center gap-2 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
                <span>Pay with</span>
                <span className="rounded-md border px-2 py-0.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Card</span>
                <span className="rounded-md border px-2 py-0.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Apple Pay</span>
                <span className="rounded-md border px-2 py-0.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Google Pay</span>
              </div>
            )}
            <p className="text-[11px] mt-3" style={{ color: "var(--gf-text-muted)" }} data-testid="cart-auto-charge-notice">
              {T && T.remaining_balance_cents > 0
                ? <>Each item above shows exactly when its remaining balance is charged. Update or cancel any time from My Bookings.</>
                : <>No further charge after checkout for the items in your cart.</>
              }
            </p>
          </aside>
        </div>
      </div>

      {/* Iteration 54 — price-change / availability revalidation dialog */}
      {priceChanges && priceChanges.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="cart-price-change-dialog">
          <div className="rounded-2xl border max-w-md w-full p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
            <h3 className="font-display text-xl" style={{ color: "var(--gf-text)" }}>Please review the changes</h3>
            <p className="text-sm mt-2" style={{ color: "var(--gf-text-soft)" }}>
              We checked your cart against the businesses&apos; current listings. Your authorization ticks were
              cleared — review the new amounts and authorize again before checking out.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {priceChanges.map((c, i) => (
                <li key={i} className="rounded-md border p-3" data-testid={`price-change-row-${i}`} style={{ borderColor: "var(--gf-border)", color: "var(--gf-text)" }}>
                  <div className="font-semibold">{c.service_title}</div>
                  {(c.blocked || c.removed) && (
                    <div className="text-xs mt-1" data-testid={`price-change-blocked-${i}`} style={{ color: "#c0392b" }}>
                      Can no longer be booked: {c.reason || "unavailable"}. Remove it from your cart to check out.
                    </div>
                  )}
                  {c.price_changed && (
                    <div className="text-xs mt-1" data-testid={`price-change-price-${i}`}>
                      Price: ${Number(c.old_price).toFixed(2)} → <b>${Number(c.new_price).toFixed(2)}</b>
                    </div>
                  )}
                  {c.deposit_changed && (
                    <div className="text-xs mt-1" data-testid={`price-change-deposit-${i}`}>
                      Deposit: ${Number(c.old_deposit).toFixed(2)} → <b>${Number(c.new_deposit).toFixed(2)}</b>
                    </div>
                  )}
                  {c.deposit_type_changed && (
                    <div className="text-xs mt-1" data-testid={`price-change-deposit-type-${i}`}>
                      Deposit type: {c.old_deposit_type} → <b>{c.new_deposit_type}</b>
                    </div>
                  )}
                  {c.refund_policy_changed && (
                    <div className="text-xs mt-1" data-testid={`price-change-refund-${i}`}>
                      Deposit refund policy: {c.old_refundable ? "refundable" : "non-refundable"} →{" "}
                      <b>{c.new_refundable ? "refundable" : "non-refundable"}</b>
                    </div>
                  )}
                  {c.cancellation_policy_changed && (
                    <div className="text-xs mt-1" data-testid={`price-change-cancellation-${i}`}>
                      Cancellation policy: {c.old_cancellation_policy} → <b>{c.new_cancellation_policy}</b>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-5 flex gap-2 justify-end">
              <button
                data-testid="price-change-continue"
                onClick={() => setPriceChanges(null)}
                className="text-sm rounded-full px-4 py-2 font-medium"
                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
              >Review my cart</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
